import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { 
  PaperAirplaneIcon, 
  PaperClipIcon,
  XMarkIcon,
  CheckCircleIcon,
  ArrowDownIcon,
  ArrowUturnLeftIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { format, isToday, isYesterday } from 'date-fns';
import { ko } from 'date-fns/locale';
import useAuthStore from '../store/authStore';
import api from '../services/api';
import ImageGalleryModal from './ImageGalleryModal';
import imageCompression from 'browser-image-compression';
import '../styles/chat.css';

const MessageList = ({ projectId, onFilesUploaded, scrollToBottomTrigger }) => {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [, setInputAreaHeight] = useState(0);
  const inputAreaRef = useRef(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastMessageIdRef = useRef(0);
  const pollingIntervalRef = useRef(null);

  const { isLoading, error, refetch } = useQuery(
    ['messages', projectId],
    () => api.get(`/messages/project/${projectId}?limit=500`).then(res => res.data),
    {
      enabled: !!projectId,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      staleTime: 0,
      cacheTime: 5 * 60 * 1000,
      onSuccess: (data) => {
        setMessages(data.messages || []);
        // 마지막 메시지 ID 저장
        if (data.messages && data.messages.length > 0) {
          const lastMessage = data.messages[data.messages.length - 1];
          lastMessageIdRef.current = lastMessage.id;
        }
        // 페이지가 포커스되어 있으면 읽음 처리
        if (!document.hidden) {
          markMessagesAsRead();
        }
      }
    }
  );

  // 폴링 함수
  const pollMessages = useCallback(async () => {
    if (!projectId || !lastMessageIdRef.current) return;

    try {
      const response = await api.get(`/messages/project/${projectId}/after?afterId=${lastMessageIdRef.current}`);
      
      if (response.data.messages.length > 0) {
        console.log(`[Polling] Found ${response.data.messages.length} new messages`);
        
        // 새 메시지가 있으면 쿼리 무효화
        queryClient.invalidateQueries(['messages', projectId]);
        queryClient.invalidateQueries(['messageCount', projectId]);
        
        // 마지막 메시지 ID 업데이트
        lastMessageIdRef.current = response.data.lastId;
        
        // 페이지가 숨겨져 있고 새 메시지가 다른 사용자의 것이면 알림음
        if (document.hidden) {
          const hasOtherUserMessage = response.data.messages.some(m => m.user_id !== user.id);
          if (hasOtherUserMessage) {
            try {
              const audio = new Audio('/sounds/notification.mp3');
              audio.volume = 0.5;
              await audio.play();
            } catch (e) {
              console.log('Audio play failed:', e);
            }
          }
        }
        
        // 스크롤 하단으로
        setTimeout(() => {
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
          }
        }, 100);
      }
    } catch (error) {
      console.error('[Polling] Error:', error);
    }
  }, [projectId, queryClient, user]);


  // 폴링 설정
  useEffect(() => {
    if (!projectId) return;

    console.log('[Polling] Starting message polling for project:', projectId);

    // 2초마다 폴링
    pollingIntervalRef.current = setInterval(pollMessages, 2000);

    // 페이지 포커스/블러 이벤트 처리
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // 페이지가 보이게 되면 즉시 폴링
        pollMessages();
        refetch();
        // 메시지 읽음 처리
        markMessagesAsRead();
      }
    };

    const handleFocus = () => {
      // 페이지에 포커스가 오면 즉시 폴링
      pollMessages();
      refetch();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      console.log('[Polling] Stopping message polling');
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [projectId, pollMessages, refetch]);

  useEffect(() => {
    // 메시지가 로드되면 즉시 하단으로 스크롤 (애니메이션 없이)
    if (messages.length > 0 && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      
      // 즉시 스크롤
      container.scrollTop = container.scrollHeight;
      
      // 이미지 로드 감지를 위한 MutationObserver
      const observer = new MutationObserver(() => {
        // DOM 변경 시 스크롤 조정
        const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
        if (isAtBottom || container.scrollTop === 0) {
          container.scrollTop = container.scrollHeight;
        }
      });
      
      // 관찰 시작
      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['src', 'style']
      });
      
      // 이미지 로드 완료 감지
      const images = container.querySelectorAll('img');
      let imagesToLoad = 0;
      
      images.forEach(img => {
        if (!img.complete) {
          imagesToLoad++;
          img.addEventListener('load', () => {
            imagesToLoad--;
            // 모든 이미지 로드 완료 시
            if (imagesToLoad === 0) {
              setTimeout(() => {
                container.scrollTop = container.scrollHeight;
                console.log('[MessageList] All images loaded, scrolled to bottom');
              }, 100);
            }
          }, { once: true });
          
          img.addEventListener('error', () => {
            imagesToLoad--;
          }, { once: true });
        }
      });
      
      // 2초 후 observer 정리
      setTimeout(() => {
        observer.disconnect();
        // 최종 스크롤 확인
        container.scrollTop = container.scrollHeight;
      }, 2000);
      
      return () => {
        observer.disconnect();
      };
    }
  }, [messages]);

  // 메시지 카운트 실시간 업데이트
  useQuery(
    ['messageCount', projectId],
    () => api.get(`/messages/project/${projectId}/count`).then(res => res.data.count),
    {
      enabled: !!projectId,
      refetchInterval: 5000 // 5초마다 카운트 업데이트 (부하 경감)
    }
  );
  
  // 메시지 읽음 처리
  const markMessagesAsRead = useCallback(async () => {
    if (!projectId) return;
    
    try {
      await api.put(`/messages/project/${projectId}/read`);
      // 읽지 않은 카운트 갱신
      queryClient.invalidateQueries(['unreadCount', projectId]);
    } catch (error) {
      console.error('[MarkAsRead] Error:', error);
    }
  }, [projectId, queryClient]);

  // 스크롤 버튼 표시 여부
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
      setShowScrollButton(!isNearBottom);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // 입력 영역 높이 측정
  useEffect(() => {
    const updateInputAreaHeight = () => {
      if (inputAreaRef.current) {
        const height = inputAreaRef.current.offsetHeight;
        setInputAreaHeight(height);
      }
    };

    // 초기 높이 측정
    setTimeout(updateInputAreaHeight, 100);
    
    // 리사이즈 시 재측정
    window.addEventListener('resize', updateInputAreaHeight);
    
    // DOM 변경 감지
    const observer = new MutationObserver(updateInputAreaHeight);
    if (inputAreaRef.current) {
      observer.observe(inputAreaRef.current, { 
        childList: true, 
        subtree: true, 
        attributes: true 
      });
    }

    return () => {
      window.removeEventListener('resize', updateInputAreaHeight);
      observer.disconnect();
    };
  }, [replyTo, newMessage]); // 답글이나 메시지 내용 변경 시 재측정

  const scrollToBottom = (forceInstant = false) => {
    // 컨테이너 직접 스크롤
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const targetScroll = container.scrollHeight;
      
      // 즉시 스크롤 또는 부드러운 스크롤
      if (forceInstant) {
        container.scrollTop = targetScroll;
      } else {
        container.scrollTo({
          top: targetScroll,
          behavior: 'smooth'
        });
      }
      
      console.log('[MessageList] Scrolled to:', container.scrollTop, 'of', container.scrollHeight);
    }
    
    // 백업으로 messagesEndRef 사용
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: forceInstant ? 'instant' : 'smooth',
        block: 'end'
      });
    }
  };

  // 컴포넌트 마운트 시 즉시 하단으로 이동
  useEffect(() => {
    const timer = setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    }, 0);
    return () => clearTimeout(timer);
  }, []);
  
  // scrollToBottomTrigger가 true가 될 때 스크롤
  useEffect(() => {
    if (scrollToBottomTrigger && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      
      // 여러 번 시도하여 확실하게 스크롤
      const scrollAttempts = [0, 50, 100, 200, 500];
      scrollAttempts.forEach(delay => {
        setTimeout(() => {
          if (container) {
            container.scrollTop = container.scrollHeight + 1000;
            console.log(`[Trigger ${delay}ms] Scrolled to:`, container.scrollTop);
          }
        }, delay);
      });
    }
  }, [scrollToBottomTrigger]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if ((!newMessage.trim() && selectedFiles.length === 0) || !projectId || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('content', newMessage.trim());
      
      // 답글 대상이 있으면 추가
      if (replyTo) {
        formData.append('reply_to_id', replyTo.id);
      }
      
      // 파일 추가
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      // API를 통해 메시지 전송
      const response = await api.post(`/messages/project/${projectId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data) {
        console.log('[Polling] Message sent successfully');
        
        // 입력 초기화
        setNewMessage('');
        setSelectedFiles([]);
        setReplyTo(null);
        
        // 즉시 메시지 목록 갱신
        queryClient.invalidateQueries(['messages', projectId]);
        queryClient.invalidateQueries(['messageCount', projectId]);
        
        // 파일이 업로드되었으면 프로젝트 정보 새로고침
        if (selectedFiles.length > 0 && onFilesUploaded) {
          onFilesUploaded();
        }
        
        // 스크롤
        setTimeout(() => {
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
          }
        }, 100);
      }
    } catch (error) {
      console.error('메시지 전송 실패:', error);
      toast.error('메시지 전송에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTyping = (e) => {
    setNewMessage(e.target.value);
  };

  const formatTime = (date) => {
    return format(new Date(date), 'HH:mm');
  };

  const formatMessageDate = (date) => {
    const messageDate = new Date(date);
    
    if (isToday(messageDate)) {
      return '오늘';
    } else if (isYesterday(messageDate)) {
      return '어제';
    } else {
      return format(messageDate, 'M월 d일', { locale: ko });
    }
  };

  const getUserTypeLabel = (userType) => {
    const labels = {
      admin: '관리자',
      contractor: '소장님',
      customer: '고객님',
      vip_customer: 'VIP고객님'
    };
    return labels[userType] || userType;
  };

  const getUserColor = (userType) => {
    const colors = {
      admin: 'text-purple-600',
      contractor: 'text-blue-600',
      customer: 'text-gray-600',
      vip_customer: 'text-yellow-600'
    };
    return colors[userType] || 'text-gray-600';
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    
    // 파일 개수 제한 (30개)
    if (selectedFiles.length + files.length > 30) {
      toast.warning(`최대 30개까지 업로드 가능합니다. 현재 ${selectedFiles.length}개 선택됨`);
      return;
    }
    
    // 이미지 파일 압축 처리
    const processedFiles = [];
    const compressionOptions = {
      maxSizeMB: 2, // 최대 2MB로 압축
      maxWidthOrHeight: 1920, // 최대 너비/높이 1920px
      useWebWorker: true,
      fileType: 'image/jpeg', // JPEG로 변환
      initialQuality: 0.85, // 초기 품질 85%
      alwaysKeepResolution: false, // 해상도 유지하지 않음
      preserveExif: false // EXIF 데이터 제거 (회전 정보 포함)
    };
    
    for (const file of files) {
      try {
        if (file.type.startsWith('image/')) {
          // 이미지 파일인 경우 압축
          const compressedFile = await imageCompression(file, compressionOptions);
          
          // 압축된 파일에 원본 파일명 유지
          const newFile = new File([compressedFile], file.name, {
            type: compressedFile.type || 'image/jpeg',
            lastModified: Date.now()
          });
          
          console.log(`이미지 압축 완료: ${file.name} (${Math.round(file.size/1024/1024*100)/100}MB → ${Math.round(newFile.size/1024/1024*100)/100}MB)`);
          processedFiles.push(newFile);
        } else {
          // 이미지가 아닌 파일은 그대로 추가
          processedFiles.push(file);
        }
      } catch (error) {
        console.error('이미지 압축 실패:', error);
        toast.error(`이미지 처리 실패: ${file.name}`);
        // 압축 실패 시 원본 파일 사용
        processedFiles.push(file);
      }
    }
    
    // 파일 크기 체크 (총 300MB)
    const currentSize = selectedFiles.reduce((sum, file) => sum + file.size, 0);
    const newSize = processedFiles.reduce((sum, file) => sum + file.size, 0);
    const totalSize = currentSize + newSize;
    
    if (totalSize > 300 * 1024 * 1024) {
      toast.warning(`총 파일 크기가 300MB를 초과할 수 없습니다. 현재: ${Math.round(totalSize / 1024 / 1024)}MB`);
      return;
    }
    
    setSelectedFiles(prev => [...prev, ...processedFiles]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-end justify-center bg-gray-50 pb-20">
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-start gap-2 animate-pulse">
              <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-32"></div>
                <div className="h-12 bg-gray-200 rounded-2xl w-48"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <p className="text-gray-500 mb-2">메시지를 불러올 수 없습니다</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary btn-sm"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gradient-to-b from-blue-50 via-white to-blue-50 flex flex-col">
      {/* 메시지 목록 - flex-1로 남은 공간 차지 */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-4 py-4"
      >
        <div className="space-y-3">
          {messages.map((message, index) => {
            const isOwnMessage = message.user_id === user.id;
            const currentDate = formatMessageDate(message.created_at);
            const prevMessage = index > 0 ? messages[index - 1] : null;
            const prevDate = prevMessage ? formatMessageDate(prevMessage.created_at) : null;
            const showDate = currentDate !== prevDate;
            const isSameUser = prevMessage?.user_id === message.user_id;
            const isClose = prevMessage && 
              new Date(message.created_at) - new Date(prevMessage.created_at) < 60000;
            const showAvatar = !isOwnMessage && (!isSameUser || !isClose || showDate);
            
            return (
              <div key={message.id}>
                {showDate && (
                  <div className="flex items-center justify-center my-6">
                    <div className="bg-gradient-to-r from-blue-100 to-indigo-100 backdrop-blur-sm px-4 py-2 rounded-full text-xs font-medium text-blue-700 shadow-sm border border-blue-200">
                      {currentDate}
                    </div>
                  </div>
                )}
                
                <div className={`flex items-end gap-2 ${isOwnMessage ? 'justify-end' : 'justify-start'} ${
                  !showAvatar && !isOwnMessage ? 'ml-10' : ''
                }`}>
                  {/* 아바타 */}
                  {!isOwnMessage && showAvatar && (
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-sm font-bold text-white shadow-md flex-shrink-0 border-2 border-white">
                      {message.User?.name?.[0] || '?'}
                    </div>
                  )}
                  
                  <div className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'} max-w-[70%]`}>
                    {/* 발신자 정보 */}
                    {!isOwnMessage && showAvatar && (
                      <div className="flex items-center gap-2 mb-1.5 ml-3">
                        <span className="text-xs font-bold text-gray-800">
                          {message.User?.name}
                        </span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${getUserColor(message.User?.user_type)} bg-opacity-20`}>
                          {getUserTypeLabel(message.User?.user_type)}
                        </span>
                      </div>
                    )}
                    
                    {/* 답글 표시 */}
                    {message.replyTo && (
                      <div className={`reply-indicator ${isOwnMessage ? 'reply-indicator-sent' : 'reply-indicator-received'}`}>
                        <div className="flex items-center gap-1">
                          <ArrowUturnLeftIcon className="h-3 w-3 flex-shrink-0" />
                          <div className="flex items-center gap-1 overflow-hidden">
                            <span className="text-[11px] font-medium whitespace-nowrap">{message.replyTo.User?.name}:</span>
                            <span className="text-[11px] truncate">{message.replyTo.content}</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* 메시지 버블 */}
                    <div className={`group relative ${isOwnMessage ? 'chat-bubble-sent' : 'chat-bubble-received'}`}>
                      {/* 버튼 그룹 - 답글과 삭제 */}
                      <div className={`message-buttons ${isOwnMessage ? 'message-buttons-left' : 'message-buttons-right'}`}>
                        {/* 답글 버튼 */}
                        <button
                          onClick={() => {
                            setReplyTo(message);
                            // 입력창으로 포커스 이동
                            setTimeout(() => {
                              const input = document.querySelector('input[type="text"][placeholder="메시지를 입력하세요"]');
                              if (input) input.focus();
                            }, 100);
                          }}
                          className="reply-button"
                        >
                          <ArrowUturnLeftIcon className="h-3 w-3" />
                          <span>답변</span>
                        </button>
                        
                        {/* 삭제 버튼 - 자신의 메시지이거나 관리자인 경우 */}
                        {((message.User?.id === user?.id) || (message.user?.id === user?.id) || (user?.user_type === 'admin')) && !message.is_deleted && (
                          <button
                            onClick={async () => {
                              if (window.confirm('메시지를 삭제하시겠습니까?\n삭제된 메시지는 복구할 수 없습니다.')) {
                                try {
                                  await api.delete(`/messages/${message.id}`);
                                  
                                  // 메시지 목록 새로고침
                                  queryClient.invalidateQueries(['messages', projectId]);
                                  
                                  // 파일이 있는 메시지인 경우 프로젝트 정보도 새로고침
                                  if (message.MessageFiles && message.MessageFiles.length > 0 && onFilesUploaded) {
                                    onFilesUploaded();
                                  }
                                  
                                  toast.success('메시지가 삭제되었습니다');
                                } catch (error) {
                                  console.error('메시지 삭제 오류:', error);
                                  toast.error('메시지 삭제에 실패했습니다');
                                }
                              }
                            }}
                            className="delete-button"
                          >
                            <TrashIcon className="h-3 w-3" />
                            <span>삭제</span>
                          </button>
                        )}
                      </div>
                      
                      {/* 메시지 내용 */}
                      <div className="break-words">
                        {message.content}
                      </div>
                      
                      {/* 시간 및 읽음 표시 */}
                      <div className={`flex items-center gap-1 mt-1 text-[10px] ${
                        isOwnMessage ? 'text-primary-200 justify-end' : 'text-gray-400'
                      }`}>
                        <span>{formatTime(message.created_at)}</span>
                        {isOwnMessage && message.is_read && (
                          <CheckCircleIcon className="h-3 w-3" />
                        )}
                      </div>
                      
                      {/* 수정/삭제 표시 */}
                      {(message.is_edited || message.is_deleted) && (
                        <div className={`text-[10px] mt-0.5 ${
                          isOwnMessage ? 'text-primary-200' : 'text-gray-400'
                        }`}>
                          {message.is_edited && '(수정됨)'}
                          {message.is_deleted && '(삭제됨)'}
                        </div>
                      )}
                    </div>
                    
                    {/* 파일 첨부 - 이미지 그룹 레이아웃 */}
                    {message.MessageFiles && message.MessageFiles.length > 0 && (() => {
                      const getFilePath = (path) => {
                        if (!path) return '';
                        if (path.startsWith('http')) return path;
                        
                        // 개발 환경에서는 백엔드 서버 주소를 붙여줌
                        const baseUrl = process.env.NODE_ENV === 'development' 
                          ? 'http://localhost:5000' 
                          : '';
                        
                        // 경로가 /로 시작하면 그대로 사용
                        if (path.startsWith('/')) return baseUrl + path;
                        // uploads/로 시작하면 앞에 / 추가
                        return baseUrl + '/' + path;
                      };
                      
                      const imageFiles = message.MessageFiles.filter(f => f.file_type === 'image');
                      const docFiles = message.MessageFiles.filter(f => f.file_type !== 'image');
                      
                      return (
                        <div className="mt-3">
                          {/* 이미지 파일 그리드 */}
                          {imageFiles.length > 0 && (
                            <div className={`${
                              imageFiles.length === 1 
                                ? 'block' 
                                : 'grid grid-cols-2 gap-2'
                            } max-w-sm`}>
                              {imageFiles.slice(0, 4).map((file, idx) => (
                                <div key={file.id} className="relative">
                                  <div 
                                    className="relative group cursor-pointer rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow border-2 border-white"
                                    onClick={() => {
                                      setSelectedImage({
                                        ...file,
                                        src: getFilePath(file.file_path),
                                        name: file.file_name
                                      });
                                      setGalleryImages(imageFiles.map(f => ({
                                        ...f,
                                        src: getFilePath(f.file_path),
                                        name: f.file_name
                                      })));
                                      setCurrentImageIndex(idx);
                                      setShowImageModal(true);
                                    }}
                                  >
                                    <picture>
                                      {file.thumbnail_medium_webp && (
                                        <source 
                                          srcSet={getFilePath(file.thumbnail_medium_webp)} 
                                          type="image/webp" 
                                        />
                                      )}
                                      <img 
                                        src={getFilePath(file.thumbnail_medium || file.file_path)}
                                        alt={file.file_name}
                                        className={`w-full ${
                                          imageFiles.length === 1 
                                            ? 'h-auto max-h-56' 
                                            : 'h-36'
                                        } object-cover hover:scale-105 transition-transform duration-300`}
                                        style={{ imageOrientation: 'from-image' }}
                                        loading="lazy"
                                    onLoad={() => {
                                      // 이미지 로드 완료 시 스크롤 위치 조정
                                      const isLastMessage = index === messages.length - 1;
                                      const isLastFile = idx === imageFiles.length - 1;
                                      
                                      // 마지막 메시지의 마지막 이미지가 로드되면 스크롤을 최하단으로
                                      if (isLastMessage && isLastFile) {
                                        // requestAnimationFrame을 사용하여 DOM 업데이트 후 스크롤
                                        requestAnimationFrame(() => {
                                          const scrollHeight = Math.max(
                                            document.documentElement.scrollHeight,
                                            document.body.scrollHeight,
                                            document.documentElement.offsetHeight,
                                            document.body.offsetHeight,
                                            document.documentElement.clientHeight
                                          );
                                          
                                          window.scrollTo({
                                            top: scrollHeight,
                                            behavior: 'instant'
                                          });
                                        });
                                      }
                                    }}
                                      />
                                    </picture>
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200"></div>
                                  </div>
                                  {/* 4번째 이미지에 +N 오버레이 */}
                                  {idx === 3 && imageFiles.length > 4 && (
                                    <div 
                                      className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center cursor-pointer"
                                      onClick={() => {
                                        setSelectedImage({
                                          ...imageFiles[3],
                                          src: getFilePath(imageFiles[3].file_path),
                                          name: imageFiles[3].file_name
                                        });
                                        setGalleryImages(imageFiles.map(f => ({
                                          ...f,
                                          src: getFilePath(f.file_path),
                                          name: f.file_name
                                        })));
                                        setCurrentImageIndex(3);
                                        setShowImageModal(true);
                                      }}
                                    >
                                      <span className="text-white font-bold text-2xl">
                                        +{imageFiles.length - 4}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* 문서 파일 목록 */}
                          {docFiles.length > 0 && (
                            <div className={`${imageFiles.length > 0 ? 'mt-3' : ''} space-y-2`}>
                              {docFiles.map((file) => (
                                <a
                                  key={file.id}
                                  href={getFilePath(file.file_path)}
                                  download={file.file_name}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 rounded-xl transition-all duration-200 max-w-xs shadow-sm border border-blue-100 hover:shadow-md"
                                >
                                  <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center shadow-sm">
                                    <PaperClipIcon className="h-5 w-5 text-white" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-800 truncate">
                                      {file.file_name}
                                    </p>
                                    <p className="text-xs text-blue-600 font-medium">
                                      {file.file_size > 0 ? `${(file.file_size / 1024 / 1024).toFixed(1)}MB` : '파일'}
                                    </p>
                                  </div>
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            );
          })}
          
          <div ref={messagesEndRef} />
          {/* 메시지 하단 여백 - 입력창에 가리지 않도록 */}
          <div className="h-4" />
        </div>
      </div>

      {/* 스크롤 다운 버튼 - 개선된 디자인 */}
      {showScrollButton && (
        <button
          onClick={() => scrollToBottom(false)}
          className="fixed bottom-36 right-4 bg-blue-500 hover:bg-blue-600 text-white shadow-lg rounded-full p-3 transition-all duration-300 hover:scale-110 group z-50"
        >
          <ArrowDownIcon className="h-5 w-5 animate-bounce" />
          {/* 새 메시지 알림 배지 */}
          {messages.length > 0 && (
            <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full animate-pulse"></span>
          )}
        </button>
      )}

      {/* 선택된 파일 미리보기 */}
      {selectedFiles.length > 0 && (
        <div className="flex-shrink-0 bg-gradient-to-r from-blue-50 to-indigo-50 border-t border-blue-200 px-4 py-3 max-h-36 overflow-y-auto">
          <div className="flex items-center justify-between mb-3 px-1">
            <div className="flex items-center gap-3 text-xs text-blue-700">
              <span className="font-bold">{selectedFiles.length}개 파일 선택됨</span>
              <span className="font-medium">
                총 {Math.round(selectedFiles.reduce((sum, file) => sum + file.size, 0) / 1024 / 1024)}MB
              </span>
            </div>
            <button
              onClick={() => setSelectedFiles([])}
              className="text-xs text-red-600 hover:text-red-700 font-bold px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
            >
              모두 삭제
            </button>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {selectedFiles.map((file, index) => (
              <div key={index} className="relative group flex-shrink-0">
                <div className="w-16 h-16 bg-gradient-to-br from-white to-gray-50 rounded-xl flex items-center justify-center shadow-sm border border-gray-200">
                  {file.type.startsWith('image/') ? (
                    <img 
                      src={URL.createObjectURL(file)} 
                      alt={file.name}
                      className="w-full h-full object-cover rounded-xl"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                      <PaperClipIcon className="h-5 w-5 text-white" />
                    </div>
                  )}
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-md hover:scale-110"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 메시지 입력 - 개선된 디자인 */}
      <div ref={inputAreaRef} className="flex-shrink-0 bg-white border-t border-gray-100 pb-1 relative">
        {/* 상단 그라데이션 효과 */}
        <div className="absolute -top-4 left-0 right-0 h-4 bg-gradient-to-t from-white/80 to-transparent pointer-events-none" />
        {/* 답글 표시 */}
        {replyTo && (
          <div className="px-4 pt-2 pb-1">
            <div className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl px-3 py-2 border border-blue-200 shadow-sm">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                  <ArrowUturnLeftIcon className="h-3 w-3 text-white flex-shrink-0" />
                </div>
                <div className="flex items-center gap-1 text-xs">
                  <span className="font-bold text-blue-800">{replyTo.User?.name}:</span>
                  <span className="text-blue-600 truncate">{replyTo.content}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setReplyTo(null)}
                className="p-1 hover:bg-blue-100 rounded-full transition-colors"
              >
                <XMarkIcon className="h-3 w-3 text-blue-600" />
              </button>
            </div>
          </div>
        )}
        <form onSubmit={handleSendMessage} className="flex items-center gap-2 px-3 pt-2 pb-1.5">
          <div className="flex-1 relative">
            <input
              type="text"
              value={newMessage}
              onChange={handleTyping}
              placeholder="메시지를 입력하세요..."
              className="w-full h-[42px] pl-4 pr-12 bg-gray-50 border border-gray-200 rounded-full text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white focus:border-transparent transition-all shadow-sm"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-100 rounded-full transition-all hover:scale-110"
              title="파일 첨부"
            >
              <PaperClipIcon className="h-5 w-5 text-gray-500 hover:text-blue-500" />
            </button>
          </div>
          
          <button
            type="submit"
            disabled={(!newMessage.trim() && selectedFiles.length === 0) || isSubmitting}
            className={`h-[42px] w-[42px] flex items-center justify-center rounded-full transition-all shadow-md ${
              newMessage.trim() || selectedFiles.length > 0
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 hover:scale-110' 
                : 'bg-gray-200 text-gray-400'
            }`}
            title="메시지 전송"
          >
            <PaperAirplaneIcon className="h-5 w-5 -rotate-45" />
          </button>
          
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.hwp,.hwpx,.xls,.xlsx,.ppt,.pptx,.dwg,.dxf,.zip,.rar,.7z,.txt,.rtf,.csv,.xml,.json"
            onChange={handleFileSelect}
            className="hidden"
          />
        </form>
      </div>

      {/* Portal 이미지 갤러리 모달 */}
      <ImageGalleryModal 
        isOpen={showImageModal}
        images={galleryImages.map(img => {
          const path = img.thumbnail_medium || img.file_path || img.src;
          if (!path) return '';
          if (path.startsWith('http')) return path;
          if (path.startsWith('/')) return path;
          if (path.startsWith('uploads/')) return '/' + path;
          return '/' + path;
        })}
        currentIndex={currentImageIndex}
        projectId={projectId}
        onClose={() => {
          setShowImageModal(false);
          setSelectedImage(null);
          setGalleryImages([]);
          setCurrentImageIndex(0);
        }}
        onIndexChange={(index) => {
          setCurrentImageIndex(index);
          if (galleryImages[index]) {
            setSelectedImage(galleryImages[index]);
          }
        }}
      />

      {/* 기존 갤러리 모달 (임시 주석처리) */}
      {false && (
        <div className="fixed inset-0 z-[9999] bg-black" style={{ 
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          width: '100vw',
          height: '100vh',
          position: 'fixed'
        }}>
          <div className="relative h-full flex flex-col" style={{ 
            height: '100vh',
            paddingTop: 'env(safe-area-inset-top, 0px)'
          }}>
            {/* 헤더 - 모바일 안전 영역 확보 */}
            <div className="relative bg-gradient-to-b from-black to-black/80 z-30 flex-shrink-0" style={{ 
              paddingTop: '60px',
              paddingLeft: 'env(safe-area-inset-left, 0px)',
              paddingRight: 'env(safe-area-inset-right, 0px)'
            }}>
              <div className="px-4 pb-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-white font-bold text-base">
                    사진 {galleryImages.length}장
                  </h3>
                  <button
                    onClick={() => {
                      setShowImageModal(false);
                      setSelectedImage(null);
                      setGalleryImages([]);
                      setCurrentImageIndex(0);
                    }}
                    className="p-3 -mr-2 hover:bg-white/10 rounded-full transition-colors"
                  >
                    <XMarkIcon className="h-7 w-7 text-white" style={{ strokeWidth: 2 }} />
                  </button>
                </div>
              </div>
            </div>

            {/* 콘텐츠 영역 */}
            <div className="flex-1 overflow-hidden relative" style={{ 
              paddingLeft: 'env(safe-area-inset-left, 0px)',
              paddingRight: 'env(safe-area-inset-right, 0px)'
            }}>
              {selectedImage ? (
                /* 단일 이미지 뷰어 */
                <div className="h-full flex flex-col">
                  <div className="flex-1 relative flex items-center justify-center"
                       onTouchStart={(e) => setTouchStart(e.targetTouches[0].clientX)}
                       onTouchMove={(e) => setTouchEnd(e.targetTouches[0].clientX)}
                       onTouchEnd={() => {
                         if (!touchStart || !touchEnd) return;
                         const distance = touchStart - touchEnd;
                         const minSwipeDistance = 50;
                         
                         if (Math.abs(distance) > minSwipeDistance) {
                           if (distance > 0 && currentImageIndex < galleryImages.length - 1) {
                             // 왼쪽 스와이프 - 다음 이미지
                             setCurrentImageIndex(currentImageIndex + 1);
                             setSelectedImage(galleryImages[currentImageIndex + 1]);
                           } else if (distance < 0 && currentImageIndex > 0) {
                             // 오른쪽 스와이프 - 이전 이미지
                             setCurrentImageIndex(currentImageIndex - 1);
                             setSelectedImage(galleryImages[currentImageIndex - 1]);
                           }
                         }
                       }}>
                    
                    {/* PC용 왼쪽 화살표 */}
                    {currentImageIndex > 0 && (
                      <button
                        onClick={() => {
                          setCurrentImageIndex(currentImageIndex - 1);
                          setSelectedImage(galleryImages[currentImageIndex - 1]);
                        }}
                        className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 rounded-full transition-colors hidden md:block z-10"
                      >
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                    )}
                    
                    {/* 이미지 */}
                    <img 
                      src={selectedImage.src}
                      alt={selectedImage.name}
                      className="max-w-full max-h-full object-contain select-none"
                      style={{ imageOrientation: 'from-image' }}
                      draggable="false"
                      onClick={() => {
                        // 모바일에서는 탭하면 UI 토글
                        if (window.innerWidth < 768) {
                          setSelectedImage(null);
                        }
                      }}
                    />
                    
                    {/* PC용 오른쪽 화살표 */}
                    {currentImageIndex < galleryImages.length - 1 && (
                      <button
                        onClick={() => {
                          setCurrentImageIndex(currentImageIndex + 1);
                          setSelectedImage(galleryImages[currentImageIndex + 1]);
                        }}
                        className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 rounded-full transition-colors hidden md:block z-10"
                      >
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    )}
                    
                    {/* 이미지 카운터 */}
                    <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/60 rounded-full">
                      <span className="text-white text-sm font-medium">
                        {currentImageIndex + 1} / {galleryImages.length}
                      </span>
                    </div>
                  </div>
                  
                  <div className="bg-black/90 p-4" style={{ 
                    paddingBottom: 'max(env(safe-area-inset-bottom, 20px), 20px)' 
                  }}>
                    <p className="text-white text-sm text-center mb-2">{selectedImage.name}</p>
                    <button
                      onClick={() => setSelectedImage(null)}
                      className="w-full py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors font-medium"
                    >
                      썸네일로 돌아가기
                    </button>
                  </div>
                </div>
              ) : (
                /* 썸네일 그리드 */
                <div className="h-full overflow-y-auto p-4" style={{
                  paddingBottom: 'calc(80px + env(safe-area-inset-bottom, 0px))'
                }}>
                  <div className="grid grid-cols-3 gap-2">
                    {galleryImages.map((image, index) => (
                      <button
                        key={image.id || index}
                        onClick={() => {
                          setSelectedImage(image);
                          setCurrentImageIndex(index);
                        }}
                        className="aspect-square overflow-hidden rounded-lg bg-slate-900"
                      >
                        <img 
                          src={(() => {
                            const path = image.thumbnail_medium || image.file_path || image.src;
                            if (!path) return '';
                            if (path.startsWith('http')) return path;
                            if (path.startsWith('/')) return path;
                            if (path.startsWith('uploads/')) return '/' + path;
                            return '/' + path;
                          })()}
                          alt={image.name || image.file_name}
                          className="w-full h-full object-cover hover:opacity-80 transition-opacity"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* 하단 닫기 버튼 - 썸네일 그리드에서만 표시 */}
            {!selectedImage && (
              <div className="absolute bottom-0 left-0 right-0 bg-black/95 border-t border-gray-800 flex-shrink-0" style={{ 
                paddingBottom: 'max(env(safe-area-inset-bottom, 20px), 20px)',
                paddingLeft: 'env(safe-area-inset-left, 0px)',
                paddingRight: 'env(safe-area-inset-right, 0px)'
              }}>
                <div className="px-4 pt-4">
                  <button
                    onClick={() => {
                      setShowImageModal(false);
                      setSelectedImage(null);
                      setGalleryImages([]);
                      setCurrentImageIndex(0);
                    }}
                    className="w-full py-3 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors font-medium"
                  >
                    닫기
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageList;